import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import { equationPartsToLatex, removePointsAndParenthesis } from '~/bootcamp/Activities/Algebra/Algebra.utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useUnlikeInput from './useUnlikeInput'

function UnlikeInput({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { equationParts, answer, formValid, inputRef, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useUnlikeInput({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
      answerState,
      autofocus,
    })

  useEmitInstance(
    onInstanceChange,
    equationParts.length > 0
      ? `${equationPartsToLatex(
          equationParts.map((part) => ({ ...part, exponent: part.variable ? 1 : undefined })),
        )} = \\square`
      : null,
  )

  const [inputKey, setInputKey] = useState<string>(uuid())

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <div className="flex flex-row items-center mt-10">
          <span className="block sm:inline-block text-center">
            {equationParts &&
              equationParts.map(({ integer, variable }, i) => (
                <span className="leading-10" key={i.toString()}>
                  {(i > 0 || integer < 0) && ` ${integer < 0 ? '-' : '+'} `}
                  {!variable || Math.abs(integer) !== 1 ? Math.abs(integer) : ''}
                  <i>{variable}</i>
                </span>
              ))}
            <Equals />
            <Input
              aria-label={t('ariaLabelButtons.insertSimplifiedExpression')}
              autoFocus={autofocus}
              className="min-w-50"
              isError={answerState === false}
              key={inputKey}
              maxDigits={40}
              onChange={(value) => onInputChange(removePointsAndParenthesis(value))}
              onEnter={onEnterPress}
              ref={inputRef}
              type="text"
              value={answer}
            />
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange('clear'),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default UnlikeInput
